import {isEqual} from 'es-toolkit'
import {compact, every, some, without} from 'es-toolkit/compat'

import config from '../config'

import {ProviderId, ShippingId} from '../types'

import {OrderTagType} from '../components/order-tag/OrderTag'

import {
  InternalCreditNote,
  InternalCreditNoteV2,
  InternalOrder,
  InternalOrderInvoice,
  InternalOrderInvoiceV2,
  InternalOrderOrderRefund,
} from '../internal-order-types'
import {
  AdditionalCostId,
  BackendModel,
  ModelDimensions,
  Order,
  OrderCreditNote,
  OrderCreditNoteV2,
  OrderInvoice,
  OrderInvoiceV2,
  OrderStatusType,
  Quote,
  RefundQuote,
} from '../order-types'

const infillRegex = /^infill_(.*)$/
const toleranceRegex = /^tolerance_(.*)$/

export const getQuoteTags = (quote: Quote, model: BackendModel) => {
  const tags: OrderTagType[] = []
  if ((!!quote.note && quote.note !== '') || (model.attachments ?? []).length > 0) {
    tags.push('detailsAttached')
  }
  if (quote.scale !== 1) {
    tags.push('needsScaling')
  }
  if ((quote.types || []).includes('economy')) {
    tags.push('economy')
  }
  if ((quote.types || []).includes('expedited')) {
    tags.push('expedited')
  }
  if ((quote.types || []).find(value => infillRegex.test(value))) {
    tags.push('infill')
  }
  if ((quote.types || []).find(value => toleranceRegex.test(value))) {
    tags.push('tolerance')
  }
  return tags
}

export const getOrderTags = (order: Order) => {
  const tags: OrderTagType[] = []

  if (order.note) {
    tags.push('orderNoteAttached')
  } else if (
    some(order.quotes, quote => !!quote.note && quote.note !== '') ||
    some(order.models, model => !!model.attachments && model.attachments.length > 0)
  ) {
    tags.push('detailsAttached')
  }
  if (!every(order.quotes, ['scale', 1])) {
    tags.push('needsScaling')
  }
  if (some(order.quotes, quote => (quote.types || []).find(value => value === 'economy'))) {
    tags.push('economy')
  }
  if (some(order.quotes, quote => (quote.types || []).find(value => value === 'expedited'))) {
    tags.push('expedited')
  }
  if (some(order.quotes, quote => (quote.types || []).find(value => infillRegex.test(value)))) {
    tags.push('infill')
  }
  if (some(order.quotes, quote => (quote.types || []).find(value => toleranceRegex.test(value)))) {
    tags.push('tolerance')
  }
  if (order.shipping.type === 'express') {
    tags.push('express')
  }
  if (order.additionalCosts && order.additionalCosts.length > 0) {
    tags.push('hasAdditionalCost')
  }
  if (order.isManual) {
    tags.push('isManualOrder')
  }
  if (config.refund.enabled && order.refundRequests && order.refundRequests.length > 0) {
    tags.push('hasRefundRequest')
  }
  return tags
}

export const getOrderOnlyTags = (tags: OrderTagType[]) =>
  without(tags, 'expedited', 'economy', 'needsScaling', 'detailsAttached')

const isOrderStatusType = (value: string) =>
  Object.values(OrderStatusType).includes(value as OrderStatusType)

// Removes unknown order status and consecutive duplicates caused by updates within certain statuses
export const sanitizeHistory = (order: Order) => {
  const onlyKnownStatus = order.history.filter(item => isOrderStatusType(item.status))
  return onlyKnownStatus.filter(
    (item, index) => index === 0 || item.status !== onlyKnownStatus[index - 1].status
  )
}

export const scaleModelDimensions = (dimensions: ModelDimensions, scale: number) => ({
  x: dimensions.x * scale,
  y: dimensions.y * scale,
  z: dimensions.z * scale,
})

const getProductionTimeFast = (quotes: (Quote | undefined)[]) =>
  compact(quotes).reduce((value, {productionTimeFast}) => Math.max(productionTimeFast, value), 0)

const getProductionTimeSlow = (quotes: (Quote | undefined)[]) =>
  compact(quotes).reduce((value, {productionTimeSlow}) => Math.max(productionTimeSlow, value), 0)

export const getProductionTime = (quotes: (Quote | undefined)[]) => {
  const productionTimeFast = getProductionTimeFast(quotes)
  const productionTimeSlow = getProductionTimeSlow(quotes)
  if (productionTimeFast === 0 || productionTimeSlow === 0) {
    return '0'
  }
  return `${productionTimeFast}-${productionTimeSlow}`
}

export const getProductionOption = (quote: Quote) => {
  if (!quote.types) {
    return null
  }
  return quote.types.includes('expedited')
    ? 'expedited'
    : quote.types.includes('economy')
      ? 'economy'
      : null
}

export const statusIsClosedOrShipped = (status: OrderStatusType) =>
  status === OrderStatusType.SHIPPED ||
  status === OrderStatusType.RECEIVED ||
  status === OrderStatusType.CANCELLED

export const isValidForAdditionalCost = (order: Order) => {
  const wasClosedBefore =
    sanitizeHistory(order).filter(item => statusIsClosedOrShipped(item.status)).length > 0

  return !statusIsClosedOrShipped(order.printingService.status) && !wasClosedBefore
}

export const hasUnreadMessages = (order: Order) =>
  !!order.messages && some(order.messages, 'unread')

export const countUnreadMessages = (order: Order) =>
  order.messages ? order.messages.filter(m => m.unread).length : 0

export const isClosed = (order: Order) =>
  order &&
  (order.printingService.status === OrderStatusType.RECEIVED ||
    order.printingService.status === OrderStatusType.CANCELLED)

// Refunds
export const getFullRefundAmount = (order: Order) =>
  (order.amounts.cartItems?.netPrice || 0) +
  (order.amounts.shipping?.netPrice || 0) +
  (order.amounts.additionalCosts?.netPrice || 0)

export const getPartialRefundAmount = (
  order: Order,
  quotes?: RefundQuote[],
  additionalCosts?: AdditionalCostId[],
  shippings?: ShippingId[]
) => {
  const quotesAmount =
    quotes && quotes.length > 0
      ? quotes.reduce((total, quote) => {
          const orderQuote = order.quotes.find(
            q =>
              q.quoteId === quote.quoteId &&
              ((!q.types && quote.types.length === 0) || isEqual(q.types, quote.types))
          )
          return total + (orderQuote?.amounts.netPrice || 0)
        }, 0)
      : 0

  const additionalCostsAmount =
    additionalCosts && additionalCosts.length > 0
      ? additionalCosts.reduce((total, additionalCostId) => {
          const additionalCost = (order.additionalCosts || []).find(
            ac => ac.id === additionalCostId
          )
          return total + (additionalCost?.amounts.netPrice || 0)
        }, 0)
      : 0

  const shippingAmount = shippings && shippings.length > 0 ? order.amounts.shipping?.netPrice : 0

  return quotesAmount + additionalCostsAmount + shippingAmount
}

export type TrackingInfoHistoryItem = {
  date: string
  trackingNumber: string | undefined
  trackingUrl: string | undefined
}

export const getTrackingInfoHistory = (order: Order) =>
  order.history.reduce((acc, item) => {
    if (item.additionalData?.trackingNumber || item.additionalData?.trackingUrl) {
      acc.push({
        date: item.date,
        trackingNumber: item.additionalData?.trackingNumber,
        trackingUrl: item.additionalData?.trackingUrl,
      })
    }
    return acc
  }, [] as TrackingInfoHistoryItem[])

export const transformInternalOrder = (
  internalOrder: InternalOrder,
  providerId: ProviderId
): Order => {
  const customerCurrency =
    internalOrder.amounts?.cartItems?.[providerId]?.customer.currency ||
    internalOrder.amounts?.shipping?.[providerId]?.customer.currency ||
    internalOrder.amounts?.additionalCosts?.[providerId]?.customer.currency ||
    ''

  const orderRefundRequests = internalOrder.refundRequests
    ? internalOrder.refundRequests[providerId]
    : undefined

  const order = {
    orderNumber: internalOrder.orderNumber,
    orderId: internalOrder.orderId,
    appId: internalOrder.appId,
    created: internalOrder.created,
    hasReview: internalOrder.hasReview[providerId],
    isManual: internalOrder.isManual,
    shippingDueDate: internalOrder.shippingDueDate[providerId],
    note: internalOrder.note,
    printingService: {
      ...internalOrder.printingService[providerId],
      status: Object.values(OrderStatusType).some(
        v => v === internalOrder.printingService[providerId].status
      )
        ? (internalOrder.printingService[providerId].status as OrderStatusType)
        : (OrderStatusType.ORDERED as OrderStatusType),
    },
    history: internalOrder.history
      .filter(historyItem =>
        Object.values(OrderStatusType).some(
          v => historyItem.status === 'printFailed' || v === historyItem.status
        )
      )
      .map(historyItem => ({
        ...historyItem,
        status:
          historyItem.status === 'printFailed'
            ? OrderStatusType.ORDERED
            : (historyItem.status as OrderStatusType),
      })),
    quotes: internalOrder.quotes.map(quote => ({
      ...quote,
      amounts: quote.amounts.vendor,
    })),
    models: internalOrder.models.map(model => ({
      modelId: model.modelId,
      fileName: model.fileName,
      fileUnit: model.fileUnit,
      area: model.area,
      volume: model.volume,
      dimensions: model.dimensions,
      thumbnailUrl: model.thumbnailUrl,
      sceneId: model.sceneId,
      attachments: model.attachments,
      cncAnalysis: model.cncAnalysis,
    })),
    amounts: {
      cartItems: internalOrder.amounts.cartItems[providerId]?.vendor || {},
      shipping: internalOrder.amounts.shipping[providerId]?.vendor || {},
      additionalCosts: internalOrder.amounts.additionalCosts?.[providerId]?.vendor,
      invoiceable:
        internalOrder.amounts.cartItems[providerId]?.invoiceable &&
        internalOrder.amounts.shipping[providerId]?.invoiceable
          ? {
              production: internalOrder.amounts.cartItems[providerId].invoiceable.netPrice,
              shipping: internalOrder.amounts.shipping[providerId].invoiceable.netPrice,
              additionalCosts:
                internalOrder.amounts.additionalCosts?.[providerId]?.invoiceable?.netPrice || 0,
            }
          : undefined,
    },
    messages: internalOrder.messages
      ? internalOrder.messages[providerId].map(message => ({
          ...message,
          unread: message.unread.partner,
        }))
      : undefined,
    additionalCosts: internalOrder.additionalCosts?.map(additionalCost => ({
      ...additionalCost,
      amounts: additionalCost.amounts.vendor,
    })),
    refundRequests: orderRefundRequests?.map((refund: InternalOrderOrderRefund) => ({
      ...refund,
      totalNetAmount: refund.totalNetAmount.vendor.net,
    })),
    customer: {
      ...internalOrder.user.shipping,
      currency: customerCurrency,
      vatId: internalOrder.user.billing.vatId,
    },
    shipping: internalOrder.shippings[0]
      ? {
          ...internalOrder.shippings[0],
          amounts: internalOrder.shippings[0].amounts.vendor,
        }
      : {},
    customsInformation: internalOrder.customsInformation,
  }

  return order
}

export const transformInternalOrderInvoice = (
  internalOrderInvoice: InternalOrderInvoice
): OrderInvoice => {
  const invoice = {
    invoiceId: internalOrderInvoice.invoiceId,
    orderId: internalOrderInvoice.orderId,
    orderNumber: internalOrderInvoice.orderNumber,
    vendorId: internalOrderInvoice.vendorId,
    orderDate: internalOrderInvoice.orderDate,
    created: internalOrderInvoice.created,
    amounts: internalOrderInvoice.amounts.vendor,
    providerInvoiceables: internalOrderInvoice.providerInvoiceables,
  }

  return invoice
}

export const transformInternalOrderInvoiceV2 = (
  internalOrderInvoice: InternalOrderInvoiceV2
): OrderInvoiceV2 => {
  const invoice = {
    invoiceId: internalOrderInvoice.invoiceId,
    orderId: internalOrderInvoice.orderId,
    orderNumber: internalOrderInvoice.orderNumber,
    vendorId: internalOrderInvoice.vendorId,
    orderDate: internalOrderInvoice.orderDate,
    created: internalOrderInvoice.created,
    amounts: internalOrderInvoice.amounts.vendor,
    invoiceable: {
      additionalCosts: internalOrderInvoice.amounts.invoiceable.additionalCostsTotal,
      production: internalOrderInvoice.amounts.invoiceable.subTotal,
      shipping: internalOrderInvoice.amounts.invoiceable.shippingTotal,
      total: internalOrderInvoice.amounts.invoiceable.net,
    },
  }

  return invoice
}

export const transformInternalCreditNote = (
  internalCreditNote: InternalCreditNote
): OrderCreditNote => ({
  created: internalCreditNote.created,
  orderId: internalCreditNote.orderId,
  orderNumber: internalCreditNote.orderNumber,
  amounts: internalCreditNote.amounts.vendor,
  providerInvoiceables: internalCreditNote.providerInvoiceables,
})

export const transformInternalCreditNoteV2 = (
  internalCreditNote: InternalCreditNoteV2
): OrderCreditNoteV2 => ({
  created: internalCreditNote.created,
  orderId: internalCreditNote.orderId,
  orderNumber: internalCreditNote.orderNumber,
  amounts: internalCreditNote.amounts.vendor,
  invoiceable: {
    additionalCosts: internalCreditNote.amounts.invoiceable.additionalCostsTotal,
    production: internalCreditNote.amounts.invoiceable.subTotal,
    shipping: internalCreditNote.amounts.invoiceable.shippingTotal,
    total: internalCreditNote.amounts.invoiceable.net,
  },
})
